import React, { useEffect, useRef, useState } from 'react';
import { hooks, RootState } from '../hooks';
import { exportOfflineQueue, shareOrDownload } from '../services/offlineExportService';
import { offlineQueue } from '../services/offlineQueueService';
import { setFailedCount } from '../store/slices/offlineSlice';
import { useLanguage } from '../context/LanguageContext';

export const OfflineBanner: React.FC = () => {
  const { t } = useLanguage();
  const o = t.offline;
  const { isOnline, pendingCount, failedCount } = hooks.useAppSelector(
    (state: RootState) => state.offlineSlice
  );
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const dispatch = hooks.useAppDispatch();

  const [showOnlineFlash, setShowOnlineFlash] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const [shareModal, setShareModal] = useState<{ blob: Blob; filename: string } | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const prevOnline = useRef(true);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!prevOnline.current && isOnline) {
      setShowOnlineFlash(true);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setShowOnlineFlash(false), 3000);
    }
    prevOnline.current = isOnline;
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
      if (msgTimer.current) clearTimeout(msgTimer.current);
    };
  }, [isOnline]);

  const isFlash = isOnline && showOnlineFlash;
  const hasFailedItems = failedCount > 0;

  const handleCancelFailed = async () => {
    await offlineQueue.clearFailed();
    dispatch(setFailedCount(0));
    setShowCancelConfirm(false);
  };

  // Show failed-items banner (persistent, even when online)
  if (isOnline && !showOnlineFlash) {
    if (!hasFailedItems && !shareModal) return null;
    return (
      <>
        {hasFailedItems && (
          <FailedExportBanner
            failedCount={failedCount}
            isExporting={isExporting}
            exportMsg={exportMsg}
            onExport={() => handleExport(authSlice, setIsExporting, setExportMsg, msgTimer, setShareModal, {
              shared: o.sharedSuccess,
              saved: o.savedToDownloads,
              failed: o.exportFailed,
            })}
            onCancelRequest={() => setShowCancelConfirm(true)}
            strings={o}
          />
        )}
        {shareModal && (
          <ShareFallbackModal
            filename={shareModal.filename}
            blob={shareModal.blob}
            onClose={() => setShareModal(null)}
            strings={o}
          />
        )}
        {showCancelConfirm && (
          <CancelConfirmModal
            onConfirm={handleCancelFailed}
            onClose={() => setShowCancelConfirm(false)}
            strings={o}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        data-offline-banner="true"
        style={{
          position: 'fixed',
          top: 52,
          left: 0,
          right: 0,
          zIndex: 999,
          backgroundColor: isFlash ? '#22c55e' : '#f97316',
          color: '#fff',
          textAlign: 'center',
          padding: '6px 16px',
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'background-color 0.3s ease',
        }}
      >
        {isFlash ? (
          <>
            <span>●</span>
            <span>
              {o.backOnline}
              {pendingCount > 0 ? ` · ${o.syncing.replace('{count}', String(pendingCount))}` : ''}
            </span>
          </>
        ) : (
          <>
            <span>◌</span>
            <span>
              {o.offlineMode}
              {pendingCount > 0 ? ` · ${o.pendingItems.replace('{count}', String(pendingCount))}` : ''}
            </span>
            {pendingCount > 0 && (
              <ExportButton
                isExporting={isExporting}
                exportMsg={exportMsg}
                shareLabel={o.share}
                onExport={() => handleExport(authSlice, setIsExporting, setExportMsg, msgTimer, setShareModal, {
                  shared: o.sharedSuccess,
                  saved: o.savedToDownloads,
                  failed: o.exportFailed,
                })}
              />
            )}
          </>
        )}
      </div>
      {shareModal && (
        <ShareFallbackModal
          filename={shareModal.filename}
          blob={shareModal.blob}
          onClose={() => setShareModal(null)}
          strings={o}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

async function handleExport(
  authSlice: any,
  setIsExporting: (v: boolean) => void,
  setExportMsg: (v: string | null) => void,
  msgTimer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  setShareModal: (v: { blob: Blob; filename: string } | null) => void,
  strings: { shared: string; saved: string; failed: string },
) {
  const userId = authSlice.userId || 'unknown';
  const userName = authSlice.userName || 'User';
  setIsExporting(true);
  setExportMsg(null);
  try {
    const result = await exportOfflineQueue(userId, userName);
    const shareResult = await shareOrDownload(result.blob, result.filename);

    if (shareResult.type === 'shared') {
      setExportMsg(strings.shared);
    } else if (shareResult.type === 'saved') {
      setExportMsg(strings.saved);
    } else if (shareResult.type === 'downloaded') {
      setExportMsg(`✓ ${result.filename}`);
    } else if (shareResult.type === 'needs_modal') {
      setShareModal({ blob: result.blob, filename: result.filename });
      return;
    }
  } catch (err: any) {
    if ((err as any)?.name === 'AbortError') return;
    setExportMsg(`✗ ${err.message || strings.failed}`);
  } finally {
    setIsExporting(false);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setExportMsg(null), 5000);
  }
}

// ─────────────────────────────────────────────────────────────────────────────

const ExportButton: React.FC<{
  isExporting: boolean;
  exportMsg: string | null;
  shareLabel: string;
  onExport: () => void;
}> = ({ isExporting, exportMsg, shareLabel, onExport }) => (
  <button
    onClick={onExport}
    disabled={isExporting}
    style={{
      marginLeft: 8,
      padding: '2px 10px',
      fontSize: 12,
      fontWeight: 700,
      background: 'rgba(255,255,255,0.25)',
      border: '1px solid rgba(255,255,255,0.6)',
      borderRadius: 4,
      color: '#fff',
      cursor: isExporting ? 'not-allowed' : 'pointer',
      opacity: isExporting ? 0.7 : 1,
    }}
  >
    {isExporting ? '...' : exportMsg ? exportMsg : shareLabel}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Modal fallback saat native share tidak tersedia (Android WebView lama, dll.)
// ─────────────────────────────────────────────────────────────────────────────

const ShareFallbackModal: React.FC<{
  filename: string;
  blob: Blob;
  onClose: () => void;
  strings: Record<string, string>;
}> = ({ filename, blob, onClose, strings: s }) => {
  const [shareErr, setShareErr] = React.useState(false);

  const handleShareFile = async () => {
    const file = new File([blob], filename, { type: 'application/json' });
    try {
      await navigator.share({ files: [file], title: filename });
      onClose();
    } catch (e) {
      if ((e as any)?.name !== 'AbortError') setShareErr(true);
      else onClose();
    }
  };

  const canShareFiles = typeof navigator.share === 'function';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', backgroundColor: '#fff',
          borderRadius: '20px 20px 0 0',
          padding: '24px 20px 40px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#D1D5DB', margin: '0 auto 20px' }} />
        <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px', textAlign: 'center' }}>
          {s.shareExportTitle}
        </p>
        <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 4px', textAlign: 'center' }}>
          {filename}
        </p>
        <p style={{ fontSize: 11, color: '#16A34A', margin: '0 0 20px', textAlign: 'center' }}>
          {s.fileSavedToDownloads}
        </p>

        {canShareFiles && !shareErr && (
          <button
            onClick={handleShareFile}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px', borderRadius: 12,
              backgroundColor: '#EFF6FF', marginBottom: 12,
              width: '100%', border: '1.5px solid #BFDBFE', cursor: 'pointer', textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 26 }}>📤</span>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1D4ED8', margin: 0 }}>{s.shareFile}</p>
              <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>{s.shareFileDesc}</p>
            </div>
          </button>
        )}

        {(shareErr || !canShareFiles) && (
          <div style={{
            padding: '12px 14px', borderRadius: 12,
            backgroundColor: '#FEF9C3', marginBottom: 12,
            border: '1px solid #FDE047',
          }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#92400E', margin: '0 0 6px' }}>{s.howToShare}</p>
            <p style={{ fontSize: 12, color: '#78350F', margin: 0, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: s.howToShareDesc.replace('{filename}', `<strong>${filename}</strong>`) }}
            />
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '12px', borderRadius: 12,
            border: 'none', backgroundColor: '#F3F4F6',
            fontSize: 14, fontWeight: 600, color: '#6B7280', cursor: 'pointer',
          }}
        >
          {s.close}
        </button>
      </div>
    </div>
  );
};

const CancelConfirmModal: React.FC<{
  onConfirm: () => void;
  onClose: () => void;
  strings: Record<string, string>;
}> = ({ onConfirm, onClose, strings: s }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}
    onClick={onClose}
  >
    <div
      style={{
        width: '100%', maxWidth: 320, backgroundColor: '#fff',
        borderRadius: 16, padding: '24px 20px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)', textAlign: 'center',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
      <p style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
        {s.cancelFailedTitle}
      </p>
      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 24px', lineHeight: 1.6 }}>
        {s.cancelFailedDesc}
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            flex: 1, padding: '12px', borderRadius: 10,
            border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB',
            fontSize: 14, fontWeight: 600, color: '#6B7280', cursor: 'pointer',
          }}
        >
          {s.no}
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1, padding: '12px', borderRadius: 10,
            border: 'none', backgroundColor: '#EF4444',
            fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
          }}
        >
          {s.yesCancelBtn}
        </button>
      </div>
    </div>
  </div>
);

const FailedExportBanner: React.FC<{
  failedCount: number;
  isExporting: boolean;
  exportMsg: string | null;
  onExport: () => void;
  onCancelRequest: () => void;
  strings: Record<string, string>;
}> = ({ failedCount, isExporting, exportMsg, onExport, onCancelRequest, strings: s }) => (
  <div
    data-offline-banner="true"
    style={{
      position: 'fixed',
      top: 52,
      left: 0,
      right: 0,
      zIndex: 999,
      backgroundColor: '#ef4444',
      color: '#fff',
      padding: '6px 12px',
      fontSize: 13,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}
  >
    <span>⚠</span>
    <span style={{ flex: 1 }}>{s.failedBannerMsg.replace('{count}', String(failedCount))}</span>
    <ExportButton isExporting={isExporting} exportMsg={exportMsg} shareLabel={s.share} onExport={onExport} />
    <button
      onClick={onCancelRequest}
      title={s.cancelFailedTooltip}
      style={{
        padding: '2px 8px',
        fontSize: 12,
        fontWeight: 700,
        background: 'rgba(0,0,0,0.2)',
        border: '1px solid rgba(255,255,255,0.4)',
        borderRadius: 4,
        color: '#fff',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {s.cancelFailedBtn}
    </button>
  </div>
);
